/* eslint-disable react/jsx-pascal-case */
import React, { useState, useEffect, useRef } from 'react';
import StepPreviewOverlay from '../shared/StepPreviewOverlay';
import { Lock } from 'lucide-react';
import { useSubscription } from '../../context/SubscriptionContext';
import Step1_Context from './Step1_Context';
import Step2_ArticleInput from './Step2_ArticleInput';
import Step3_MatrixBuilder from './Step3_MatrixBuilder';
import Step4_GapConfirmation from './Step4_GapConfirmation';
import Step5_ValidityScorer from './Step5_ValidityScorer';
import Step6_GapFormula from './Step6_GapFormula';
import GapOutput from './GapOutput';
import './GapFinderStyles.css';

import { processAllArticles, generateGapWarnings } from '../../utils/gapDetectionEngine';
import { buildFullMatrix, buildRadarData, buildBarData } from '../../utils/matrixBuilder';
import { scoreValidity, applyUserConfirmation } from '../../utils/gapScorer';
import { generateGapStatement, generateGapFormula } from '../../utils/gapStatementGenerator';
import { useAuth } from '../../context/AuthContext';
import { useWizard } from '../../context/WizardContext';

const STEPS = [
  { id: 1, label: 'Context' },
  { id: 2, label: 'Literature' },
  { id: 3, label: 'Matrix' },
  { id: 4, label: 'Confirm' },
  { id: 5, label: 'Validity' },
  { id: 6, label: 'Formula' },
  { id: 7, label: 'Report' },
];


const getInitialState = () => ({
  context: {
    topic: '',
    discipline: '',
    academicLevel: '',
    keywords: Array(10).fill(''),
  },
  articles: Array(10).fill(null).map((_, i) => ({
    id: Date.now() + i,
    title: '',
    authors: '',
    year: '',
    abstract: '',
    limitations: '',
  })),
  matrixData: null,
  processedArticles: [],
  rankedGaps: [],
  confirmations: {},
  validityScores: {},
  gapFormula: '',
  gapStatement: '',
  warnings: [],
  adjustedGaps: [],
});

const LS_KEY = 'gapFinder_state_v1';

export default function GapFinderWizard() {
  const { currentUser } = useAuth();
  const { currentDesignName, saveCurrentDesign } = useWizard();
  const { getWizardMaxStep, triggerUpgradeModal } = useSubscription();
  const maxGFStep = getWizardMaxStep('GF');
  const dbSaveTimerRef = useRef(null);
  const [step, setStep] = useState(1);
  const [previewStep, setPreviewStep] = useState(null);
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (!saved) return getInitialState();
      const loaded = JSON.parse(saved);
      const defaults = getInitialState();
      // Deep-merge: ensure ALL required fields exist even with old/partial saves
      return {
        ...defaults,
        ...loaded,
        context: {
          ...defaults.context,
          ...(loaded.context || {}),
          keywords: Array.isArray(loaded.context?.keywords)
            ? loaded.context.keywords
            : defaults.context.keywords,
        },
        articles:
          Array.isArray(loaded.articles) && loaded.articles.length >= 10
            ? loaded.articles
            : defaults.articles,
        processedArticles: Array.isArray(loaded.processedArticles)
          ? loaded.processedArticles
          : [],
        rankedGaps: Array.isArray(loaded.rankedGaps) ? loaded.rankedGaps : [],
        warnings: Array.isArray(loaded.warnings) ? loaded.warnings : [],
        adjustedGaps: Array.isArray(loaded.adjustedGaps)
          ? loaded.adjustedGaps
          : [],
        confirmations:
          loaded.confirmations && typeof loaded.confirmations === 'object'
            ? loaded.confirmations
            : {},
        validityScores:
          loaded.validityScores && typeof loaded.validityScores === 'object'
            ? loaded.validityScores
            : {},
        gapFormula: typeof loaded.gapFormula === 'string' ? loaded.gapFormula : '',
        gapStatement: typeof loaded.gapStatement === 'string' ? loaded.gapStatement : '',
        matrixData: loaded.matrixData || null,
      };
    } catch {
      return getInitialState();
    }
  });

  // Persist to localStorage (always — works for guests too)
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(state));
    } catch {}
  }, [state]);

  // Autosave Gap Finder data to DB (logged-in users, debounced 4s)
  useEffect(() => {
    if (!currentUser) return;
    if (dbSaveTimerRef.current) clearTimeout(dbSaveTimerRef.current);
    dbSaveTimerRef.current = setTimeout(() => {
      saveCurrentDesign(currentDesignName || 'Untitled Design').catch(() => {});
    }, 4000);
    return () => {
      if (dbSaveTimerRef.current) clearTimeout(dbSaveTimerRef.current);
    };
  }, [state, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateState = (patch) => setState((s) => ({ ...s, ...patch }));

  /* ─── Phase 3: Run analysis ─── */
  const runAnalysis = () => {
    const { context, articles } = state;
    const keywords = (context.keywords || []).filter((k) => k.trim());
    const validArticles = articles.filter(
      (a) => a.title.trim() && a.authors.trim() && a.year.trim() && a.abstract.trim()
    );

    const processed = processAllArticles(validArticles, keywords);
    const { matrixRows, rankedGaps } = buildFullMatrix(processed);
    const radarData = buildRadarData(rankedGaps);
    const barData = buildBarData(rankedGaps);
    const warnings = generateGapWarnings(rankedGaps, processed);

    // Enrich rankedGaps with top keywords
    const enriched = rankedGaps.map((g) => {
      const topKws = [];
      processed.forEach((a) => {
        (a.matchedKeywords?.[g.type] || []).forEach((m) => {
          if (!topKws.includes(m.phrase)) topKws.push(m.phrase);
        });
      });
      return { ...g, topKeywords: topKws.slice(0, 5) };
    });

    updateState({
      processedArticles: processed,
      rankedGaps: enriched,
      matrixData: { matrixRows, rankedGaps: enriched, barData, radarData },
      warnings,
    });
  };

  /* ─── Phase 7: Generate full output ─── */
  const generateOutput = () => {
    const { context, processedArticles, rankedGaps, confirmations, validityScores, gapFormula } = state;
    const adjusted = applyUserConfirmation(rankedGaps, confirmations);
    const validityResult = scoreValidity(validityScores);

    const topGap = adjusted[0];
    const secondGap = adjusted[1];
    const thirdGap = adjusted[2];

    const statement = generateGapStatement({
      topic: context.topic,
      discipline: context.discipline,
      academicLevel: context.academicLevel,
      keywords: (context.keywords || []).filter((k) => k.trim()),
      topGap,
      secondGap,
      thirdGap,
      rankedGaps: adjusted,
      processedArticles,
      validityScore: validityResult,
    });

    const formula = gapFormula || generateGapFormula({
      topGapType: topGap?.type,
      keywords: (context.keywords || []).filter((k) => k.trim()),
      topic: context.topic,
      discipline: context.discipline,
      academicLevel: context.academicLevel,
    });

    updateState({
      adjustedGaps: adjusted,
      gapStatement: statement,
      gapFormula: formula,
    });
  };

  /* ─── Navigation ─── */
  const goNext = () => {
    if (step >= maxGFStep) {
      triggerUpgradeModal('wizardGF');
      return;
    }
    if (step === 2) runAnalysis();
    if (step === 6) generateOutput();
    setStep((s) => Math.min(s + 1, 7));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    if (window.confirm('Start a new analysis? All current data will be cleared.')) {
      localStorage.removeItem(LS_KEY);
      setState(getInitialState());
      setStep(1);
    }
  };

  /* ─── Render ─── */
  const validityResult = scoreValidity(state.validityScores);

  return (
    <div className="gf-wizard">
      {/* ── Progress Stepper ── */}
      <div className="gf-stepper">
        {STEPS.map((s) => {
          const displayActive = (previewStep !== null ? previewStep : step) === s.id;
          const isDone = step > s.id;
          const isLocked = s.id > maxGFStep;
          const isAhead = s.id > step && !isLocked;
          return (
            <div
              key={s.id}
              className={`gf-stepper-item ${displayActive ? 'active' : ''} ${isDone ? 'done' : ''} ${isAhead ? 'gf-preview-step' : ''} ${isLocked ? 'gf-locked-step' : ''}`}
              onClick={() => {
                if (isLocked) {
                  triggerUpgradeModal('wizardGF');
                  return;
                }
                if (s.id <= step) {
                  setStep(s.id);
                  setPreviewStep(null);
                } else {
                  setPreviewStep(s.id);
                }
              }}
              style={{ cursor: 'pointer' }}
              title={
                isLocked
                  ? `Upgrade to unlock Step ${s.id}: ${s.label}`
                  : isAhead
                  ? `Preview Step ${s.id}: ${s.label} (read-only)`
                  : `Go to ${s.label}`
              }
            >
              <div className="gf-stepper-circle">
                {isLocked ? <Lock size={13} /> : isDone ? '✓' : s.id}
              </div>
              <span className="gf-stepper-label">{s.label}</span>
            </div>
          );
        })}
      </div>

      {/* ── Step Content ── */}
      <div className="gf-content">
        {(() => {
          const displayStep = previewStep !== null ? previewStep : step;
          const isPreview = previewStep !== null;
          const noop = () => {};

          const stepContent = (
            <>
              {displayStep === 1 && (
                <Step1_Context
                  data={state.context}
                  onChange={isPreview ? noop : (context) => updateState({ context })}
                  onNext={isPreview ? noop : goNext}
                />
              )}
              {displayStep === 2 && (
                <Step2_ArticleInput
                  articles={state.articles}
                  onChange={isPreview ? noop : (articles) => updateState({ articles })}
                  onNext={isPreview ? noop : goNext}
                  onBack={isPreview ? noop : goBack}
                />
              )}
              {displayStep === 3 && (
                <Step3_MatrixBuilder
                  matrixData={state.matrixData}
                  onNext={isPreview ? noop : goNext}
                  onBack={isPreview ? noop : goBack}
                />
              )}
              {displayStep === 4 && (
                <Step4_GapConfirmation
                  rankedGaps={state.rankedGaps}
                  confirmations={state.confirmations}
                  onChange={isPreview ? noop : (confirmations) => updateState({ confirmations })}
                  onNext={isPreview ? noop : goNext}
                  onBack={isPreview ? noop : goBack}
                />
              )}
              {displayStep === 5 && (
                <Step5_ValidityScorer
                  scores={state.validityScores}
                  onChange={isPreview ? noop : (validityScores) => updateState({ validityScores })}
                  onNext={isPreview ? noop : goNext}
                  onBack={isPreview ? noop : goBack}
                />
              )}
              {displayStep === 6 && (
                <Step6_GapFormula
                  context={state.context}
                  topGap={state.rankedGaps?.[0]}
                  formulaData={state.gapFormula}
                  onChange={isPreview ? noop : (gapFormula) => updateState({ gapFormula })}
                  onNext={isPreview ? noop : goNext}
                  onBack={isPreview ? noop : goBack}
                />
              )}
              {displayStep === 7 && (
                <GapOutput
                  context={state.context}
                  gapStatement={state.gapStatement}
                  gapFormula={state.gapFormula}
                  validityScore={validityResult}
                  warnings={state.warnings}
                  matrixData={state.matrixData}
                  adjustedGaps={state.adjustedGaps}
                  onReset={isPreview ? noop : handleReset}
                />
              )}
            </>
          );

          if (isPreview) {
            return (
              <StepPreviewOverlay
                stepNumber={previewStep}
                stepLabel={STEPS.find(s => s.id === previewStep)?.label}
                onReturn={() => setPreviewStep(null)}
              >
                {stepContent}
              </StepPreviewOverlay>
            );
          }
          return stepContent;
        })()}
      </div>

      {/* ── Save indicator ── */}
      <div className="gf-save-indicator">💾 Progress auto-saved</div>
    </div>
  );
}
